import React from "react";
import { Link } from "react-router-dom";
import { StatusErrorIcon, StatusOkIcon } from "affinityIcons";
import { isNil } from "ramda";
import { styled } from "styled-components";

import { DateTime } from "affinityUI/data/valueDisplay";
import { NavigateButton } from "affinityUI/elements/button";
import { Grid } from "affinityUI/elements/grid";
import { Restricted } from "lib/auth";
import { PatientData } from "types/patient";

const InfoActions = styled.span`
  align-items: center;
  gap: 1rem;
`;

const TrainedStatus: React.FC<{ trainingDate?: Maybe<string> }> = ({ trainingDate }) => {
  if (isNil(trainingDate)) {
    return (
      <div>
        <StatusErrorIcon /> Patient not trained
      </div>
    );
  }
  return (
    <div>
      <StatusOkIcon /> Patient trained <DateTime value={trainingDate} format={DateTime.FORMAT.DATE} />
    </div>
  );
};

const NoDevice: React.FC<{ patient: PatientData }> = ({ patient }) => {
  if (!isNil(patient.disEnrollmentDate)) {
    return (
      <div>
        <StatusErrorIcon /> Device withdrawn by {patient.disEnrolledBy?.firstName} {patient.disEnrolledBy?.lastName} on{" "}
        <DateTime value={patient.disEnrollmentDate} format={DateTime.FORMAT.DATE} />
      </div>
    );
  } else {
    return (
      <InfoActions>
        <StatusErrorIcon /> This patient <strong>does not</strong> have a device
      </InfoActions>
    );
  }
};

export const DeviceInfo: React.FC<{ patient: PatientData }> = ({ patient }) => {
  return !isNil(patient.enrollmentDate) && isNil(patient.disEnrollmentDate) ? (
    <Grid $tight>
      <div>This patient has been issued a device</div>
      <Grid $cols={{ lg: ["auto"], xs: ["max-content", "max-content"] }} $tight>
        <div>
          <StatusOkIcon /> Device issued by {patient.enrolledBy?.firstName} {patient.enrolledBy?.lastName} on{" "}
          <DateTime value={patient.enrollmentDate} format={DateTime.FORMAT.DATE} />
        </div>
        <TrainedStatus trainingDate={patient.trainingDate} />
      </Grid>
      <Restricted permits={["change_device"]}>
        <div>
          If this patient no longer requires the device, <Link to="withdraw-device">withdraw device</Link>
        </div>
      </Restricted>
    </Grid>
  ) : (
    <>
      <Restricted permits={["change_device"]} fallback={<NoDevice patient={patient} />}>
        <Grid $tight>
          <Grid $cols={{ lg: ["auto"], xs: ["auto", "max-content"] }} $tight>
            <NoDevice patient={patient} />
            <div>
              <NavigateButton to="issue-device">Issue device</NavigateButton>
            </div>
            <div>
              When you issue a device it will be sent directly to a patient&apos;s home address.{" "}
              <Link to="issue-device">More info</Link>
            </div>
          </Grid>
        </Grid>
      </Restricted>
    </>
  );
};
